import {
  skipToken,
  useMutation,
  useQuery,
  useQueryClient
} from '@tanstack/react-query'
import { useAuth } from './useAuth'
import { throwOnError } from '../utils/throwOnError'
import type { CreatePlanLegislation } from '../types/types'

export function useLegislationCatalogue(enabled: boolean) {
  const { authFetch } = useAuth()
  return useQuery({
    queryKey: ['legislationCatalogue'],
    enabled,
    staleTime: 60_000,
    queryFn: async () => {
      const { data, error } = await authFetch().GET('/legislation')
      if (error) throwOnError(error)
      return data!
    }
  })
}

export function useLegislationPreview(documentId?: string) {
  const { authFetch } = useAuth()
  return useQuery({
    queryKey: ['legislationPreview', documentId],
    // The preview must not change underneath the user's structural selections.
    staleTime: Infinity,
    gcTime: 0,
    refetchOnWindowFocus: false,
    queryFn: documentId
      ? async () => {
          const { data, error } = await authFetch().GET(
            '/legislation/preview',
            {
              params: { query: { documentId } }
            }
          )
          if (error) throwOnError(error)
          return data!
        }
      : skipToken
  })
}

export function usePlanLegislation(planId: number, enabled = true) {
  const { authFetch } = useAuth()
  return useQuery({
    queryKey: ['planLegislation', planId],
    enabled,
    staleTime: 0,
    queryFn: async () => {
      const { data, error } = await authFetch().GET(
        '/plans/{planId}/legislation',
        {
          params: { path: { planId } }
        }
      )
      if (error) throwOnError(error)
      return data!
    }
  })
}

export function useAddPlanLegislation() {
  const { authFetch } = useAuth()
  const cache = useQueryClient()
  return useMutation({
    mutationFn: async ({
      planId,
      body
    }: {
      planId: number
      body: CreatePlanLegislation
    }) => {
      const { data, error } = await authFetch().POST(
        '/plans/{planId}/legislation',
        {
          params: { path: { planId } },
          body
        }
      )
      if (error) throwOnError(error)
      return data!
    },
    // Any ancestor entry can affect several mounted descendant views.
    onSuccess: () => cache.invalidateQueries({ queryKey: ['planLegislation'] })
  })
}

export function useDeletePlanLegislation() {
  const { authFetch } = useAuth()
  const cache = useQueryClient()
  return useMutation({
    mutationFn: async ({
      planId,
      entryId,
      etag
    }: {
      planId: number
      entryId: number
      etag: string
    }) => {
      const { error } = await authFetch().DELETE(
        '/plans/{planId}/legislation/{entryId}',
        {
          params: { path: { planId, entryId }, header: { 'If-Match': etag } }
        }
      )
      if (error) throwOnError(error)
    },
    onSuccess: () => cache.invalidateQueries({ queryKey: ['planLegislation'] })
  })
}
